import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Star, FileText, Phone, Mail, Clock, Briefcase, Download,
  UserCheck, UserX, Trash2
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const getStatusColor = (status) => {
  const colors = {
    new: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    reviewed: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
    contacted: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    hired: 'text-green-400 bg-green-400/10 border-green-400/20',
    rejected: 'text-red-400 bg-red-400/10 border-red-400/20',
  };
  return colors[status] || 'text-slate-400 bg-slate-400/10 border-slate-400/20';
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminApplicationsTab = ({ applications, stats, loading, onUpdateStatus, onDelete }) => {
  const [filter, setFilter] = useState('all');
  const appStats = stats || { total: 0, new: 0, reviewed: 0, contacted: 0, hired: 0, rejected: 0 };
  const filtered = filter === 'all' ? applications : applications.filter(a => a.status === filter);

  return (
    <>
      {/* Stats */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[
          { key: 'total', label: 'Total', icon: Users, color: 'text-white' },
          { key: 'new', label: 'New', icon: Star, color: 'text-emerald-400' },
          { key: 'reviewed', label: 'Reviewed', icon: FileText, color: 'text-purple-400' },
          { key: 'contacted', label: 'Contacted', icon: Phone, color: 'text-blue-400' },
          { key: 'hired', label: 'Hired', icon: UserCheck, color: 'text-green-400' },
          { key: 'rejected', label: 'Rejected', icon: UserX, color: 'text-red-400' },
        ].map(item => (
          <div key={item.key} className="bg-brand-surface rounded-xl border border-white/10 p-5" data-testid={`app-stat-${item.key}`}>
            <div className="flex items-center gap-2 mb-2"><item.icon className={`w-4 h-4 ${item.color}`} /><span className="text-slate-400 text-xs">{item.label}</span></div>
            <p className={`text-2xl font-heading ${item.color}`}>{appStats[item.key] || 0}</p>
          </div>
        ))}
      </motion.div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="font-heading text-2xl text-white">DRIVER <span className="text-brand-red">APPLICATIONS</span></h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="app-filter-select"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent className="bg-brand-surface border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">All</SelectItem>
            <SelectItem value="new" className="text-white hover:bg-white/10">New</SelectItem>
            <SelectItem value="reviewed" className="text-white hover:bg-white/10">Reviewed</SelectItem>
            <SelectItem value="contacted" className="text-white hover:bg-white/10">Contacted</SelectItem>
            <SelectItem value="hired" className="text-white hover:bg-white/10">Hired</SelectItem>
            <SelectItem value="rejected" className="text-white hover:bg-white/10">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading applications...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-brand-surface rounded-xl border border-white/10">
          <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-400">No applications found</p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          {filtered.map((app, index) => (
            <motion.div key={app.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }} className="bg-brand-surface rounded-xl border border-white/10 p-6" data-testid={`app-card-${app.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-grow">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <h3 className="font-heading text-lg text-white">{app.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(app.status)}`}>{app.status.toUpperCase()}</span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-blue/10 text-brand-blue border border-brand-blue/20">
                      {{ none: 'No CDL', class_a: 'CDL Class A', class_b: 'CDL Class B', class_c: 'CDL Class C' }[app.cdl_type] || app.cdl_type}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-400"><Phone className="w-4 h-4 text-brand-red" /><a href={`tel:${app.phone}`} className="hover:text-white">{app.phone}</a></div>
                    <div className="flex items-center gap-2 text-slate-400"><Mail className="w-4 h-4 text-brand-blue" /><a href={`mailto:${app.email}`} className="hover:text-white">{app.email}</a></div>
                    <div className="flex items-center gap-2 text-slate-400"><Briefcase className="w-4 h-4 text-purple-400" /><span>{app.years_experience} years experience</span></div>
                    <div className="flex items-center gap-2 text-slate-400"><Clock className="w-4 h-4 text-slate-500" /><span>{formatDate(app.created_at)}</span></div>
                  </div>
                  {app.resume_url && (
                    <div className="mt-4">
                      <a href={app.resume_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-blue/10 border border-brand-blue/20 text-brand-blue hover:bg-brand-blue/20 transition-colors text-sm"
                        data-testid={`resume-link-${app.id}`}><Download className="w-4 h-4" />{app.resume_filename || 'Download Resume'}</a>
                    </div>
                  )}
                  {app.message && (
                    <div className="mt-4 flex items-start gap-2"><FileText className="w-4 h-4 text-slate-500 mt-1 flex-shrink-0" /><div><span className="text-xs text-slate-500">Message</span><p className="text-slate-400">{app.message}</p></div></div>
                  )}
                </div>
                <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                  <Select value={app.status} onValueChange={(value) => onUpdateStatus(app.id, value)}>
                    <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white" data-testid={`app-status-select-${app.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-brand-surface border-white/10">
                      <SelectItem value="new" className="text-emerald-400 hover:bg-white/10">New</SelectItem>
                      <SelectItem value="reviewed" className="text-purple-400 hover:bg-white/10">Reviewed</SelectItem>
                      <SelectItem value="contacted" className="text-blue-400 hover:bg-white/10">Contacted</SelectItem>
                      <SelectItem value="hired" className="text-green-400 hover:bg-white/10">Hired</SelectItem>
                      <SelectItem value="rejected" className="text-red-400 hover:bg-white/10">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors" data-testid={`delete-app-${app.id}`}><Trash2 className="w-5 h-5" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-brand-surface border-white/10">
                      <AlertDialogHeader><AlertDialogTitle className="text-white">Delete Application?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">This will permanently delete the application from {app.name}.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(app.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid={`confirm-delete-app-${app.id}`}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </>
  );
};

export default AdminApplicationsTab;
